import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import AlertModal from '../components/AlertModal';
import ConfirmModal from '../components/ConfirmModal';

const AlertContext = createContext();

export const useAlert = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlert must be used within an AlertProvider');
  }
  return context;
};

export const AlertProvider = ({ children }) => {
  const [alertVisible, setAlertVisible] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmLabel, setConfirmLabel] = useState('');
  const [cancelLabel, setCancelLabel] = useState('');
  const alertResolveRef = useRef(null);
  const confirmResolveRef = useRef(null);
  const showAlertRef = useRef(null);
  const showConfirmRef = useRef(null);

  const showAlert = useCallback((message) => {
    if (alertResolveRef.current) {
      alertResolveRef.current();
      alertResolveRef.current = null;
    }
    return new Promise((resolve) => {
      setAlertMessage(message);
      setAlertVisible(true);
      alertResolveRef.current = resolve;
    });
  }, []);

  const hideAlert = useCallback(() => {
    setAlertVisible(false);
    setAlertMessage('');
    if (alertResolveRef.current) {
      alertResolveRef.current();
      alertResolveRef.current = null;
    }
  }, []);

  const showConfirm = useCallback((message, options = {}) => {
    return new Promise((resolve) => {
      setConfirmMessage(message);
      setConfirmLabel(options?.confirmLabel || '');
      setCancelLabel(options?.cancelLabel || '');
      setConfirmVisible(true);
      confirmResolveRef.current = resolve;
    });
  }, []);

  const handleConfirm = useCallback(() => {
    setConfirmVisible(false);
    setConfirmMessage('');
    setConfirmLabel('');
    setCancelLabel('');
    if (confirmResolveRef.current) {
      confirmResolveRef.current(true);
      confirmResolveRef.current = null;
    }
  }, []);

  const handleCancel = useCallback(() => {
    setConfirmVisible(false);
    setConfirmMessage('');
    setConfirmLabel('');
    setCancelLabel('');
    if (confirmResolveRef.current) {
      confirmResolveRef.current(false);
      confirmResolveRef.current = null;
    }
  }, []);

  // ref에 저장
  showAlertRef.current = showAlert;
  showConfirmRef.current = showConfirm;

  // 컴포넌트 마운트 시 한 번만 window.alert/confirm 오버라이드
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const originalAlert = window.alert;
      const _originalConfirm = window.confirm;

      window.alert = (message) => {
        if (showAlertRef.current) {
          showAlertRef.current(message);
        } else {
          originalAlert(message);
        }
      };

      // window.confirm은 비동기로 변경 불가하므로 직접 사용하지 않음
      // 대신 showConfirm을 사용하도록 함
    }
  }, []);

  return (
    <AlertContext.Provider value={{ showAlert, showConfirm }}>
      {children}
      <AlertModal
        visible={alertVisible}
        message={alertMessage}
        onClose={hideAlert}
      />
      <ConfirmModal
        visible={confirmVisible}
        message={confirmMessage}
        confirmLabel={confirmLabel}
        cancelLabel={cancelLabel}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </AlertContext.Provider>
  );
};
